export const RESUME_COMMIT_TOLERANCE_SECONDS = 0.5;
export const RESUME_SEEKABLE_SAFETY_MARGIN_SECONDS = 0.05;

// A resume response is the only authority for all three values. Keeping the
// absolute and raw targets together makes the absolute -> media-clock mapping
// a one-time operation instead of something each event handler can reinterpret.
export type AuthoritativeResumeTuple = Readonly<{
  kind: "authoritative_resume_tuple";
  nextGenerationId: string;
  originSeconds: number;
  targetAbsoluteSeconds: number;
  targetRawSeconds: number;
}>;

export type PendingResumeAuthority = {
  tuple: AuthoritativeResumeTuple;
  assigned: boolean;
  resumeAfterVerification: boolean;
};

export type ResumeAuthorityDecision =
  | { action: "wait_for_authoritative_clock" }
  | { action: "wait_for_seekable_target" }
  | { action: "assign"; targetRawSeconds: number }
  | { action: "verified" };

export function authoritativeResumeTuple(input: {
  previousGenerationId: string | null | undefined;
  nextGenerationId: string | null | undefined;
  responseOriginSeconds: number;
  targetAbsoluteSeconds: number;
}): AuthoritativeResumeTuple | null {
  const previousGenerationId = input.previousGenerationId?.trim() ?? "";
  const nextGenerationId = input.nextGenerationId?.trim() ?? "";
  if (
    previousGenerationId === ""
    || nextGenerationId === ""
    || previousGenerationId === nextGenerationId
    || !Number.isFinite(input.responseOriginSeconds)
    || input.responseOriginSeconds < 0
    || !Number.isFinite(input.targetAbsoluteSeconds)
    || input.targetAbsoluteSeconds < input.responseOriginSeconds
  ) {
    return null;
  }
  const originSeconds = input.responseOriginSeconds;
  const targetAbsoluteSeconds = input.targetAbsoluteSeconds;
  return {
    kind: "authoritative_resume_tuple",
    nextGenerationId,
    originSeconds,
    targetAbsoluteSeconds,
    targetRawSeconds: targetAbsoluteSeconds - originSeconds,
  };
}

// Explicit user intent supersedes an automatic resume target without changing
// the generation clock that was independently accepted from the server. Raw
// time may be negative here: that is a valid signal that the user selected a
// point outside this generation and the caller must perform a session handoff.
export function retargetResumeAuthority(
  authority: PendingResumeAuthority,
  targetAbsoluteSeconds: number,
  resumeAfterVerification: boolean,
): PendingResumeAuthority | null {
  if (!Number.isFinite(targetAbsoluteSeconds) || targetAbsoluteSeconds < 0) {
    return null;
  }
  return {
    tuple: {
      ...authority.tuple,
      targetAbsoluteSeconds,
      targetRawSeconds: targetAbsoluteSeconds - authority.tuple.originSeconds,
    },
    assigned: false,
    resumeAfterVerification,
  };
}

export function evaluateResumeAuthority(input: {
  authority: PendingResumeAuthority;
  attachedGenerationId: string | null | undefined;
  attachedOriginSeconds: number;
  currentRawSeconds: number;
  seekableRanges: readonly Readonly<{ startSeconds: number; endSeconds: number }>[];
}): ResumeAuthorityDecision {
  const { authority } = input;
  if (
    input.attachedGenerationId?.trim() !== authority.tuple.nextGenerationId
    || !Number.isFinite(input.attachedOriginSeconds)
    || Math.abs(input.attachedOriginSeconds - authority.tuple.originSeconds) > Number.EPSILON
  ) {
    return { action: "wait_for_authoritative_clock" };
  }

  const target = authority.tuple.targetRawSeconds;
  const seekable = input.seekableRanges.some((range) => {
    if (!Number.isFinite(range.startSeconds) || !Number.isFinite(range.endSeconds)) {
      return false;
    }
    return target >= range.startSeconds
      && target <= Math.max(range.startSeconds, range.endSeconds - RESUME_SEEKABLE_SAFETY_MARGIN_SECONDS);
  });
  if (!seekable) {
    return { action: "wait_for_seekable_target" };
  }
  if (
    authority.assigned
    && Number.isFinite(input.currentRawSeconds)
    && Math.abs(input.currentRawSeconds - target) <= RESUME_COMMIT_TOLERANCE_SECONDS
  ) {
    return { action: "verified" };
  }
  return { action: "assign", targetRawSeconds: target };
}
